
import { supabase } from "@/integrations/supabase/client";

/**
 * Storage utility functions for environment-agnostic file handling
 */

/**
 * Constructs a public URL for a file in Supabase storage
 * @param bucketName - The storage bucket name
 * @param filePath - The file path within the bucket
 * @returns The public URL for the file
 */
export const getStorageUrl = (bucketName: string, filePath: string): string => {
  if (!filePath) return "";
  
  // Remove bucket name from the beginning of filePath if it exists
  // This prevents duplication when the path already includes the bucket name
  const cleanPath = filePath.startsWith(`${bucketName}/`) 
    ? filePath.substring(bucketName.length + 1) 
    : filePath;
  
  const { data } = supabase.storage
    .from(bucketName)
    .getPublicUrl(cleanPath);
  
  return data.publicUrl;
};

/**
 * Extracts the file path from a Supabase storage URL
 * @param url - The full storage URL
 * @returns The file path or null if extraction fails
 */
export const extractStoragePath = (url: string): string | null => {
  if (!url) return null;
  
  try {
    // Handle both old and new URL formats
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split('/');
    
    // Find the storage index in the path
    const storageIndex = pathParts.findIndex(part => part === 'storage');
    
    if (storageIndex !== -1 && pathParts[storageIndex + 3]) {
      // Return path after bucket name (skip /storage/v1/object/public/bucket-name/)
      return pathParts.slice(storageIndex + 4).join('/');
    }
    
    return null;
  } catch (error) {
    console.error('Failed to extract storage path from URL:', url, error);
    return null;
  }
};

/**
 * Determines if a URL is a Supabase storage URL
 * @param url - The URL to check
 * @returns True if it's a Supabase storage URL
 */
export const isStorageUrl = (url: string): boolean => {
  if (!url) return false;
  return url.includes('supabase.co/storage/') || url.includes('/storage/v1/object/public/');
};

/**
 * Converts a full storage URL to just the path, or returns the input if it's already a path
 * @param urlOrPath - Either a full URL or already a path
 * @returns The storage path
 */
export const normalizeToPath = (urlOrPath: string): string => {
  if (!urlOrPath) return "";
  
  // If it's already a path (doesn't start with http), return as-is
  if (!urlOrPath.startsWith('http')) {
    return urlOrPath;
  }
  
  // Extract path from URL
  const path = extractStoragePath(urlOrPath);
  return path || urlOrPath;
};

/**
 * Storage bucket names used in the application
 */
export const STORAGE_BUCKETS = {
  EPISODE_THUMBNAILS: 'episode-thumbnails',
  CHARACTER_IMAGES: 'character-images',
  PARTNER_LOGOS: 'partner-logos',
  ACTIVITIES: 'activities',
} as const;
